import React from 'react';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Checkbox,
  IconButton,
  Stack,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  getLearnedRequiredKeysValidationError,
  hasLearnedAutoFillMappingValue,
  type LearnedAutoFillMergeItem,
  type LearnedAutoFillPatternV1,
} from '../../../../shared/lib/quickFill/learnedAutoFillLibrary';
import {
  WorkspaceStatusPill,
  type WorkspaceTone,
} from '../../../../shared/ui/workflow/WorkflowWorkspacePrimitives';
import type { QuickFillKeyLibraryEditableField } from '../../lib/quickFillKeyLibraryDrafts';
import {
  getKeyLibraryIconButtonSx,
  getKeyLibraryInlineEditFieldSx,
} from './quickFillKeyLibraryStyles';

interface QuickFillKeyLibraryEntryRowProps {
  entry: LearnedAutoFillPatternV1;
  mergeItem?: LearnedAutoFillMergeItem;
  isManageMode: boolean;
  isShadowed: boolean;
  isConflictConfirmed: boolean;
  isSelected?: boolean;
  onSelectedChange?: (selected: boolean) => void;
  onChange: (field: QuickFillKeyLibraryEditableField, value: string) => void;
  onDelete: () => void;
  onConfirmConflict: (confirmed: boolean) => void;
}

interface EntryFieldProps {
  entry: LearnedAutoFillPatternV1;
  field: QuickFillKeyLibraryEditableField;
  label: string;
  value: string;
  error?: boolean;
  onChange: (field: QuickFillKeyLibraryEditableField, value: string) => void;
}

const EntryField: React.FC<EntryFieldProps> = ({
  entry,
  field,
  label,
  value,
  error,
  onChange,
}) => (
  <TextField
    variant="outlined"
    size="small"
    value={value}
    error={error}
    placeholder={label}
    onChange={(event) => onChange(field, event.target.value)}
    inputProps={{ 'aria-label': `${label} for ${entry.sourceFamilyName || 'new key'}` }}
    sx={getKeyLibraryInlineEditFieldSx}
  />
);

const statusTone = (status: LearnedAutoFillMergeItem['status']): WorkspaceTone => {
  if (status === 'new') return 'success';
  if (status === 'conflict') return 'warning';
  return 'neutral';
};

const statusLabel = (status: LearnedAutoFillMergeItem['status']): string => {
  if (status === 'new') return 'New';
  if (status === 'conflict') return 'Replace';
  return 'Identical';
};

const QuickFillKeyLibraryEntryRow: React.FC<QuickFillKeyLibraryEntryRowProps> = ({
  entry,
  mergeItem,
  isManageMode,
  isShadowed,
  isConflictConfirmed,
  isSelected = false,
  onSelectedChange,
  onChange,
  onDelete,
  onConfirmConflict,
}) => {
  const keysError = getLearnedRequiredKeysValidationError(entry.requiredKeys);
  const mappingValueError = !hasLearnedAutoFillMappingValue(entry.values);
  const isConflict = mergeItem?.status === 'conflict';

  return (
    <TableRow hover selected={isSelected}>
      {isManageMode && (
        <TableCell padding="checkbox">
          <Checkbox
            size="small"
            checked={isSelected}
            onChange={(event) => onSelectedChange?.(event.target.checked)}
            inputProps={{ 'aria-label': `Select ${entry.sourceFamilyName || 'new key'}` }}
          />
        </TableCell>
      )}
      <TableCell>
        {isManageMode ? (
          <EntryField
            entry={entry}
            field="sourceFamilyName"
            label="Family name"
            value={entry.sourceFamilyName}
            error={!entry.sourceFamilyName.trim()}
            onChange={onChange}
          />
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 650 }}>
            {entry.sourceFamilyName}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <EntryField
          entry={entry}
          field="requiredKeys"
          label="Required keys"
          value={entry.requiredKeys.join(' ')}
          error={Boolean(keysError)}
          onChange={onChange}
        />
        {keysError && (
          <Typography variant="caption" color="error" display="block">
            {keysError}
          </Typography>
        )}
      </TableCell>
      <TableCell>
        <EntryField
          entry={entry}
          field="objectType"
          label="OBJECT-TYPE"
          value={entry.values.objectType}
          error={mappingValueError}
          onChange={onChange}
        />
      </TableCell>
      <TableCell>
        <EntryField
          entry={entry}
          field="skey"
          label="SKEY"
          value={entry.values.skey}
          error={mappingValueError}
          onChange={onChange}
        />
      </TableCell>
      <TableCell>
        <Stack direction="row" spacing={0.75} sx={{ width: '100%', minWidth: 0 }}>
          {(['end1', 'end2', 'end3', 'end4'] as const).map((field) => (
            <EntryField
              key={field}
              entry={entry}
              field={field}
              label={field.toUpperCase()}
              value={entry.values[field] ?? ''}
              error={mappingValueError}
              onChange={onChange}
            />
          ))}
        </Stack>
      </TableCell>
      <TableCell>
        {isManageMode ? (
          <Tooltip title="Delete this local entry">
            <IconButton
              size="small"
              aria-label={`Delete ${entry.sourceFamilyName}`}
              onClick={onDelete}
              sx={getKeyLibraryIconButtonSx}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : mergeItem ? (
          <Stack spacing={0.5} alignItems="flex-start">
            <WorkspaceStatusPill
              size="compact"
              tone={statusTone(mergeItem.status)}
              label={statusLabel(mergeItem.status)}
            />
            {isShadowed && (
              <WorkspaceStatusPill size="compact" tone="warning" label="Built-in priority" />
            )}
            {isConflict && (
              <Tooltip title="Replace the existing entry with these values">
                <Box component="label" sx={{ display: 'flex', alignItems: 'center' }}>
                  <Checkbox
                    size="small"
                    checked={isConflictConfirmed}
                    onChange={(event) => onConfirmConflict(event.target.checked)}
                    inputProps={{
                      'aria-label': `Replace existing mapping for ${entry.sourceFamilyName}`,
                    }}
                  />
                  <Typography variant="caption">Confirm replacement</Typography>
                </Box>
              </Tooltip>
            )}
          </Stack>
        ) : null}
      </TableCell>
    </TableRow>
  );
};

export default QuickFillKeyLibraryEntryRow;
